import { useTheme } from "@emotion/react";
import { Box, Link, List, ListItem, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { getQuote } from "../../../util/insights/handle-export";
import { useAtom } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { baseAtom, selectInsight } from "../../../stores/jotai/base";
import AudioPlayButton from "./component/AudioPlay";
import { InsightGroup } from "./analysis-insights";
import { KeyboardArrowDown } from "@mui/icons-material";
import { useGetFindings } from "../../../tanstackQuery/queries/findings";
import { useKeycloak } from "@react-keycloak/web";

interface AnalysisInsightsProps {
  item: InsightGroup;
  scrollRef?: React.RefObject<HTMLDivElement>;
  showInsights?: boolean;
  highlightCard?: boolean;
  customStyles?: React.CSSProperties;
  collapsible?: boolean;
  onExpand?: () => void;
  isSelectable?:boolean;
  headingStyle?:object;
}

export default function AnalysisInsightCards(props: AnalysisInsightsProps) {
  const {
    item,
    scrollRef,
    showInsights = true,
    highlightCard = true,
    customStyles = {},
    collapsible = false,
    onExpand = undefined,
    isSelectable=true,
    headingStyle={}
  } = props;
  const {
    title = "Meta analysis title",
    description = "Lorem ipsum dolor sit amet consectetur adipisicing elit.",
    insights = [],
  } = item;

  const [base, setBase] = useAtom(baseAtom);
  const client = useQueryClient();
  const theme = useTheme();
  const {keycloak}=useKeycloak();

  const [expanded, setExpanded] = useState(!collapsible);
  const [quoteExpandedInsight, setQuoteExpandedInsight] = useState<Record<string, boolean>>({});
  const selectedFindingID = base?.selectedFinding?.findingID;

  const cachedFindingData = client.getQueryData([
    "insights",
    {
      groupID: base?.team?.groupID,
      userID: base?.user?.userID,
      isMetaInsightEnabled: true,
    },
  ]);

  // Determine if data is missing
  const shouldFetchFindings =
    !cachedFindingData && !!base?.team?.groupID && !!base?.user?.userID;

  // Only run query hook if data isn't already cached
  const { findings: findingsDataFromAPI } = useGetFindings(
    { accessToken: keycloak.token },
    {
      groupID: base?.team?.groupID,
      userID: base?.user?.userID,
      isMetaInsightEnabled: true,
    },
    {
      enabled: shouldFetchFindings,
    }
  );

  // Final source of truth for findings
  const findings = cachedFindingData?.findings || findingsDataFromAPI?.findings || {};

  const shouldShow = (sectionVisible: boolean) => 
    collapsible ? expanded && sectionVisible : sectionVisible;

  const descriptionBlock = (
    <Typography
      variant="body1"
      color="text.secondary"
      sx={{ fontSize: "15px", fontWeight: 500 }}
    >
      {collapsible && !expanded
        ? `${description.slice(0, 95)}${description.length > 95 ? "..." : ""}`
        : description}

      {collapsible && !expanded && description.length > 95 && (
        <span
          style={{
            color: theme?.custom?.mainButtonHighlight,
            cursor: "pointer",
            marginLeft: 4,
            whiteSpace: "nowrap",
          }}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
            onExpand?.();
          }}
        >
          Read more
        </span>
      )}
      {!item.strategicImplication &&
        !item.suggestedReframe &&
        collapsible &&
        expanded &&
        description.length > 95 && (
          <span
            style={{
              color: theme?.custom?.mainButtonHighlight,
              cursor: "pointer",
              whiteSpace: "nowrap",
              display: "inline-block",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(false);
            }}
          >
            Read less
          </span>
        )}
    </Typography>
  );

  const implicationBlock = item.strategicImplication && shouldShow(true) && (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Strategic Implementation
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {item.strategicImplication}
      </Typography>
    </Box>
  );

  const reframeBlock = item.suggestedReframe && shouldShow(true) && (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Suggested Reframe
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {item.suggestedReframe}
        {collapsible && expanded && description.length > 20 && (
          <span
            style={{
              color: theme?.custom?.mainButtonHighlight,
              cursor: "pointer",
              whiteSpace: "nowrap",
              display: "inline-block",
            }}
            onClick={() => {
              setExpanded(false);
            }}
          >
            Read less
          </span>
        )}
      </Typography>
    </Box>
  );

  const insightsBlock =
    showInsights && shouldShow(true) ? (
      <List>
        {insights.map((item, index) => {
          const finding = findings?.[item];
          const quote = getQuote(finding?.quote);
          const isOpen = !!quoteExpandedInsight[item];
          const quoted = !isOpen
            ? `“${quote?.slice(0, 120)}”${quote?.length > 120 ? "…":""}`
            : `“${quote}” `;
          return (
            <ListItem key={index} disableGutters>
              <Link
                component="button"
                sx={{
                  color: theme.custom?.mainButtonHighlight || "#1976d2",
                  cursor: "pointer",
                  textAlign: "left",
                  display: "inline-block",
                  wordBreak: "break-word",
                  textDecoration: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation()
                   setQuoteExpandedInsight(m => ({
                      ...m,
                      [item]: !m[item],
                  }));
                  if (base?.selectedFinding?.findingID !== item && isSelectable) {
                    selectInsight(setBase, findings?.[item]);
                    if (window?.__lastInsightSelection !== undefined) {
                      window.__lastInsightSelection = null;
                    }
                  }
                }}
              >
                <AudioPlayButton
                  audioBegin={parseFloat(finding?.audioBegin)}
                  audioEnd={parseFloat(finding?.audioEnd)}
                  id={finding?.findingID}
                />
                {quoted}
                {quote?.length > 120 && (isOpen ? <strong>Read less</strong> : <strong>Read more</strong>)}           
              </Link>
            </ListItem>
          );
        })}
      </List>
    ) : null;

  return (
    <>
      <Box
        ref={scrollRef}
        sx={{
          background: highlightCard ? "rgb(229, 246, 253)" : "transparent",
          border:
            highlightCard && isSelectable &&
            (insights.includes(base?.selectedFinding?.findingID) ||
              item?.parentInsightID === selectedFindingID)
              ? `2px solid ${theme.custom?.mainButtonHighlight || "#1976d2"}`
              : "1px solid transparent",
          borderRadius: 2,
          mb: isSelectable && 2,
          transition: "max-height 0.3s ease",
          cursor: "pointer",
          ...customStyles,
        }}
        onClick={() => {
          if (base?.selectedFinding?.findingID !== item?.parentInsightID && isSelectable) {
            selectInsight(setBase, findings?.[item?.parentInsightID]);
            if (window?.__lastInsightSelection !== undefined) {
              window.__lastInsightSelection = null;
            }
          }
        }}
      >
        <Box sx={{ margin: highlightCard ? "12px" : "0px" }}>
          <Typography variant="h6" component="h4" gutterBottom sx={{...headingStyle}}>
            {title}
          </Typography>

          {descriptionBlock}
          {implicationBlock}
          {reframeBlock}
          {insightsBlock}
        </Box>
      </Box>
    </>
  );
}
